import { useState, useEffect, useCallback, useRef } from 'react';
import * as leasesService from '../services/supabase/leases';

/**
 * Custom hook for managing leases
 * Automatically fetches leases for a specific property
 */
export function useLeases(propertyId) {
  const [leases, setLeases] = useState([]);
  const [activeLease, setActiveLease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  const fetchLeases = useCallback(async () => {
    if (!propertyId) {
      setLeases([]);
      setActiveLease(null);
      setLoading(false);
      return;
    }

    console.log('[useLeases] Starting fetch...', { propertyId });
    const fetchStartTime = Date.now();
    let timeoutId;

    try {
      setLoading(true);
      setError(null);

      // Safety timeout: force loading to false after 10 seconds
      timeoutId = setTimeout(() => {
        if (isMountedRef.current) {
          console.error('[useLeases] Fetch timeout after 10s - forcing loading to false');
          setLoading(false);
          setError('Request timeout - please refresh');
        }
      }, 10000);

      const [allLeases, active] = await Promise.all([
        leasesService.getLeases(propertyId),
        leasesService.getActiveLease(propertyId),
      ]);

      if (!isMountedRef.current) {
        clearTimeout(timeoutId);
        return;
      }

      clearTimeout(timeoutId);
      console.log(`[useLeases] Fetch completed in ${Date.now() - fetchStartTime}ms`);

      setLeases(allLeases);
      setActiveLease(active);
    } catch (err) {
      clearTimeout(timeoutId);
      if (isMountedRef.current) {
        console.error('[useLeases] Error fetching leases:', err);
        setError(err.message);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [propertyId]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchLeases();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchLeases]);

  const createLease = async (leaseData) => {
    try {
      const newLease = await leasesService.createLease(leaseData);
      setLeases(prev => [newLease, ...prev]);

      if (newLease.status === 'active') {
        setActiveLease(newLease);
      }

      return { success: true, data: newLease };
    } catch (err) {
      console.error('Error creating lease:', err);
      return { success: false, error: err.message };
    }
  };

  const updateLease = async (leaseId, updates) => {
    try {
      const updatedLease = await leasesService.updateLease(leaseId, updates);

      setLeases(prev =>
        prev.map(l => (l.id === leaseId ? updatedLease : l))
      );

      if (updatedLease.status === 'active') {
        setActiveLease(updatedLease);
      } else if (activeLease?.id === leaseId) {
        setActiveLease(null);
      }

      return { success: true, data: updatedLease };
    } catch (err) {
      console.error('Error updating lease:', err);
      return { success: false, error: err.message };
    }
  };

  const terminateLease = async (leaseId, reason, terminationDate) => {
    try {
      const terminated = await leasesService.terminateLease(leaseId, reason, terminationDate);

      setLeases(prev =>
        prev.map(l => (l.id === leaseId ? terminated : l))
      );

      if (activeLease?.id === leaseId) {
        setActiveLease(null);
      }

      return { success: true, data: terminated };
    } catch (err) {
      console.error('Error terminating lease:', err);
      return { success: false, error: err.message };
    }
  };

  const archiveLease = async (leaseId) => {
    try {
      const archived = await leasesService.archiveLease(leaseId);

      setLeases(prev =>
        prev.map(l => (l.id === leaseId ? archived : l))
      );

      if (activeLease?.id === leaseId) {
        setActiveLease(null);
      }

      return { success: true, data: archived };
    } catch (err) {
      console.error('Error archiving lease:', err);
      return { success: false, error: err.message };
    }
  };

  const deleteLease = async (leaseId) => {
    try {
      await leasesService.deleteLease(leaseId);
      setLeases(prev => prev.filter(l => l.id !== leaseId));

      if (activeLease?.id === leaseId) {
        setActiveLease(null);
      }

      return { success: true };
    } catch (err) {
      console.error('Error deleting lease:', err);
      return { success: false, error: err.message };
    }
  };

  const refresh = () => {
    fetchLeases();
  };

  return {
    leases,
    activeLease,
    loading,
    error,
    createLease,
    updateLease,
    terminateLease,
    archiveLease,
    deleteLease,
    refresh,
  };
}
